import React, { useState } from 'react';
import { ElectionSummary, DemographicAnalyticsData, LiveResultsResponse } from '../types';
import { api } from '../services/api';
import {
  Printer,
  Download,
  X,
  Upload,
  Image as ImageIcon,
  CheckCircle2,
  ShieldCheck,
  Award,
  BarChart3,
  Users,
  Flame,
  Clock,
  Layers,
  FileText,
  Sliders,
  Sparkles,
} from 'lucide-react';

interface OfficialReportPdfModalProps {
  isOpen: boolean;
  onClose: () => void;
  election: ElectionSummary;
  analytics: DemographicAnalyticsData | null;
  liveResults?: LiveResultsResponse | null;
  isAdmin?: boolean;
  onUpdateLogos?: (logo1Url: string, logo2Url: string) => Promise<void>;
  onUpdateSignatures?: (signatures: {
    commissionerName?: string;
    commissionerTitle?: string;
    commissionerSignatureUrl?: string;
    observerName?: string;
    observerTitle?: string;
    observerSignatureUrl?: string;
    electpollRepName?: string;
    electpollRepTitle?: string;
    electpollRepSignatureUrl?: string;
  }) => Promise<void>;
}

export const OfficialReportPdfModal: React.FC<OfficialReportPdfModalProps> = ({
  isOpen,
  onClose,
  election,
  analytics,
  liveResults,
  isAdmin = false,
  onUpdateLogos,
  onUpdateSignatures,
}) => {
  // Logo customization state
  const [logo1, setLogo1] = useState(election.logo1Url || liveResults?.logo1Url || '');
  const [logo2, setLogo2] = useState(election.logo2Url || liveResults?.logo2Url || '');
  const [uploadingLogo1, setUploadingLogo1] = useState(false);
  const [uploadingLogo2, setUploadingLogo2] = useState(false);
  const [savedLogosNotice, setSavedLogosNotice] = useState(false);

  // Signatures customization state
  const [commName, setCommName] = useState(election.commissionerName || liveResults?.commissionerName || '');
  const [commTitle, setCommTitle] = useState(election.commissionerTitle || liveResults?.commissionerTitle || 'Chief Electoral Commissioner / Returning Officer');
  const [commSig, setCommSig] = useState(election.commissionerSignatureUrl || liveResults?.commissionerSignatureUrl || '');
  const [obsName, setObsName] = useState(election.observerName || liveResults?.observerName || '');
  const [obsTitle, setObsTitle] = useState(election.observerTitle || liveResults?.observerTitle || 'Faculty / Staff Electoral Observer');
  const [obsSig, setObsSig] = useState(election.observerSignatureUrl || liveResults?.observerSignatureUrl || '');
  const [repName, setRepName] = useState(election.electpollRepName || liveResults?.electpollRepName || 'Bukola Olapade');
  const [repTitle, setRepTitle] = useState(election.electpollRepTitle || liveResults?.electpollRepTitle || 'Electpoll Platform Representative & Systems Auditor');
  const [repSig, setRepSig] = useState(election.electpollRepSignatureUrl || liveResults?.electpollRepSignatureUrl || '');

  const [uploadingCommSig, setUploadingCommSig] = useState(false);
  const [uploadingObsSig, setUploadingObsSig] = useState(false);
  const [uploadingRepSig, setUploadingRepSig] = useState(false);
  const [savedSignaturesNotice, setSavedSignaturesNotice] = useState(false);

  // Section toggle state
  const [reportScope, setReportScope] = useState<'all' | 'results' | 'demographics'>('all');
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [customizerTab, setCustomizerTab] = useState<'signatures' | 'logos'>('signatures');

  React.useEffect(() => {
    setLogo1(election.logo1Url || liveResults?.logo1Url || '');
    setLogo2(election.logo2Url || liveResults?.logo2Url || '');
    setCommName(election.commissionerName || liveResults?.commissionerName || '');
    setCommTitle(election.commissionerTitle || liveResults?.commissionerTitle || 'Chief Electoral Commissioner / Returning Officer');
    setCommSig(election.commissionerSignatureUrl || liveResults?.commissionerSignatureUrl || '');
    setObsName(election.observerName || liveResults?.observerName || '');
    setObsTitle(election.observerTitle || liveResults?.observerTitle || 'Faculty / Staff Electoral Observer');
    setObsSig(election.observerSignatureUrl || liveResults?.observerSignatureUrl || '');
    setRepName(election.electpollRepName || liveResults?.electpollRepName || 'Bukola Olapade');
    setRepTitle(election.electpollRepTitle || liveResults?.electpollRepTitle || 'Electpoll Platform Representative & Systems Auditor');
    setRepSig(election.electpollRepSignatureUrl || liveResults?.electpollRepSignatureUrl || '');
  }, [election, liveResults, isOpen]);

  if (!isOpen) return null;

  // Handle Logo Upload via Cloudinary
  const handleUploadLogo = async (file: File, target: 1 | 2) => {
    if (target === 1) setUploadingLogo1(true);
    else setUploadingLogo2(true);

    try {
      const res = await api.uploadImageToCloudinary(file);
      if (res.success && res.url) {
        if (target === 1) {
          setLogo1(res.url);
          if (onUpdateLogos) await onUpdateLogos(res.url, logo2);
        } else {
          setLogo2(res.url);
          if (onUpdateLogos) await onUpdateLogos(logo1, res.url);
        }
        setSavedLogosNotice(true);
        setTimeout(() => setSavedLogosNotice(false), 3000);
      } else {
        alert(res.error || 'Failed to upload logo image.');
      }
    } catch (err: any) {
      alert(err.message || 'Image upload error.');
    } finally {
      if (target === 1) setUploadingLogo1(false);
      else setUploadingLogo2(false);
    }
  };

  const handleSaveCustomLogos = async () => {
    if (onUpdateLogos) {
      await onUpdateLogos(logo1, logo2);
      setSavedLogosNotice(true);
      setTimeout(() => setSavedLogosNotice(false), 3000);
    }
  };

  // Handle Signature Upload via Cloudinary
  const handleUploadSignature = async (file: File, target: 'commissioner' | 'observer' | 'representative') => {
    if (target === 'commissioner') setUploadingCommSig(true);
    else if (target === 'observer') setUploadingObsSig(true);
    else setUploadingRepSig(true);

    try {
      const res = await api.uploadImageToCloudinary(file);
      if (res.success && res.url) {
        if (target === 'commissioner') {
          setCommSig(res.url);
          if (onUpdateSignatures) {
            await onUpdateSignatures({
              commissionerName: commName,
              commissionerTitle: commTitle,
              commissionerSignatureUrl: res.url,
            });
          }
        } else if (target === 'observer') {
          setObsSig(res.url);
          if (onUpdateSignatures) {
            await onUpdateSignatures({
              observerName: obsName,
              observerTitle: obsTitle,
              observerSignatureUrl: res.url,
            });
          }
        } else {
          setRepSig(res.url);
          if (onUpdateSignatures) {
            await onUpdateSignatures({
              electpollRepName: repName,
              electpollRepTitle: repTitle,
              electpollRepSignatureUrl: res.url,
            });
          }
        }
        setSavedSignaturesNotice(true);
        setTimeout(() => setSavedSignaturesNotice(false), 3000);
      } else {
        alert(res.error || 'Failed to upload signature image.');
      }
    } catch (err: any) {
      alert(err.message || 'Signature upload error.');
    } finally {
      if (target === 'commissioner') setUploadingCommSig(false);
      else if (target === 'observer') setUploadingObsSig(false);
      else setUploadingRepSig(false);
    }
  };

  const handleSaveSignatures = async () => {
    if (onUpdateSignatures) {
      await onUpdateSignatures({
        commissionerName: commName,
        commissionerTitle: commTitle,
        commissionerSignatureUrl: commSig,
        observerName: obsName,
        observerTitle: obsTitle,
        observerSignatureUrl: obsSig,
        electpollRepName: repName,
        electpollRepTitle: repTitle,
        electpollRepSignatureUrl: repSig,
      });
      setSavedSignaturesNotice(true);
      setTimeout(() => setSavedSignaturesNotice(false), 3000);
    }
  };

  // Trigger high-resolution print/PDF download with isolated iframe engine
  const handlePrint = () => {
    const printableArea = document.getElementById('official-report-printable-area');
    if (!printableArea) {
      window.print();
      return;
    }

    const cleanTitle = (election.title || 'Election').replace(/[^a-zA-Z0-9-_ ]/g, '');
    const reportTitle = `${cleanTitle} - Official Results & Demographic Report (Electpoll)`;

    // Create an isolated printing iframe
    const iframe = document.createElement('iframe');
    iframe.id = 'electpoll-pdf-print-frame';
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (!doc) {
      window.print();
      return;
    }

    // Extract all page stylesheets and styles
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map((s) => s.outerHTML)
      .join('\n');

    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${reportTitle}</title>
          <link rel="preconnect" href="https://fonts.googleapis.com">
          <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
          <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&family=Syne:wght@600;700;800&family=Caveat:wght@600;700&display=swap" rel="stylesheet">
          ${styles}
          <style>
            @page {
              size: A4 portrait;
              margin: 10mm 12mm 10mm 12mm;
            }
            html, body {
              background: #ffffff !important;
              color: #171619 !important;
              margin: 0 !important;
              padding: 0 !important;
              font-family: 'Poppins', sans-serif !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .official-pdf-document {
              box-shadow: none !important;
              padding: 12px 0 0 0 !important;
              margin: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              border: none !important;
            }
            .pdf-avoid-break, .pdf-position-card, .pdf-demo-box, .pdf-footer-container {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
          </style>
        </head>
        <body>
          <div class="official-pdf-document">
            ${printableArea.innerHTML}
          </div>
        </body>
      </html>
    `);
    doc.close();

    // Allow images & fonts to load then trigger native print
    setTimeout(() => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch (err) {
        console.error('Iframe print failed, falling back to window.print()', err);
        window.print();
      } finally {
        setTimeout(() => {
          if (iframe.parentNode) {
            document.body.removeChild(iframe);
          }
        }, 3000);
      }
    }, 450);
  };

  // Results calculations
  const positions = liveResults?.results || [];
  const totalAccredited = liveResults?.totalAccredited || election.totalAccredited || 0;
  const totalVoted = liveResults?.totalVoted || election.totalVoted || 0;
  const turnoutPercent = election.turnoutPercentage || (totalAccredited > 0 ? parseFloat(((totalVoted / totalAccredited) * 100).toFixed(1)) : 0);

  // Hourly turnout
  const hourlyData = analytics?.byHour || [];
  const peakHour = hourlyData.find((h) => h.isPeak) || [...hourlyData].sort((a, b) => b.count - a.count)[0];
  const maxHourlyCount = hourlyData.reduce((max, h) => Math.max(max, h.count), 0);

  // Demographic cohorts
  const levels = analytics?.byLevel || [];
  const genders = analytics?.byGender || [];
  const halls = analytics?.byHall || [];
  const roles = analytics?.byRole || [];

  const generationDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const generationTime = new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });

  return (
    <div className="official-pdf-modal-overlay">
      <div className="official-pdf-modal-container">
        {/* Top Control Bar (Screen Only - Hidden on Print) */}
        <header className="official-pdf-toolbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 800, color: '#FF9900', textTransform: 'uppercase', letterSpacing: '1px' }}>
                PDF Export
              </span>
              <span style={{ fontSize: '15px', fontWeight: 800, color: '#FFFFFF' }}>
                Official Report Preview
              </span>
            </div>

            {/* Scope Filter Pills */}
            <div style={{ display: 'flex', backgroundColor: '#1E1D21', padding: '3px', borderRadius: '10px', gap: '3px', border: '1px solid #2D2C31' }}>
              {[
                { id: 'all', label: 'Full Report (All Sections)' },
                { id: 'results', label: 'Results Only' },
                { id: 'demographics', label: 'Demographics Only' },
              ].map((s) => (
                <button
                  key={s.id}
                  onClick={() => setReportScope(s.id as any)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '7px',
                    border: 'none',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    backgroundColor: reportScope === s.id ? '#FF9900' : 'transparent',
                    color: reportScope === s.id ? '#171619' : '#9CA3AF',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {isAdmin && (onUpdateSignatures || onUpdateLogos) && (
              <button
                onClick={() => setShowCustomizer(!showCustomizer)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '7px 12px',
                  borderRadius: '8px',
                  border: '1px solid #3F3F46',
                  backgroundColor: showCustomizer ? '#27272A' : '#1E1D21',
                  color: '#E4E4E7',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <Sliders size={13} />
                <span>{showCustomizer ? 'Hide Customizer' : 'Customize Logos & Signatures'}</span>
              </button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={handlePrint}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '9px 20px',
                borderRadius: '10px',
                backgroundColor: '#FF9900',
                color: '#171619',
                border: 'none',
                fontWeight: 800,
                fontSize: '13px',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(255, 153, 0, 0.4)',
                transition: 'all 0.15s ease',
              }}
            >
              <Printer size={16} />
              <span>Download / Print PDF</span>
            </button>

            <button
              onClick={onClose}
              className="official-pdf-modal-close-btn"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                border: '1px solid #2D2C31',
                backgroundColor: '#1E1D21',
                color: '#A1A1AA',
                cursor: 'pointer',
              }}
              title="Close Preview"
            >
              <X size={18} />
            </button>
          </div>
        </header>

        {/* Customization Drawer (Admin Only) */}
        {isAdmin && showCustomizer && (
          <div className="official-pdf-controls-drawer">
            {/* Drawer Subtab Navigation */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  type="button"
                  onClick={() => setCustomizerTab('signatures')}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '7px',
                    border: 'none',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    backgroundColor: customizerTab === 'signatures' ? '#FF9900' : '#27272A',
                    color: customizerTab === 'signatures' ? '#171619' : '#D1D5DB',
                  }}
                >
                  Official Signatures & Signatories
                </button>
                <button
                  type="button"
                  onClick={() => setCustomizerTab('logos')}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '7px',
                    border: 'none',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    backgroundColor: customizerTab === 'logos' ? '#FF9900' : '#27272A',
                    color: customizerTab === 'logos' ? '#171619' : '#D1D5DB',
                  }}
                >
                  Header Co-Branding Logos
                </button>
              </div>

              {savedSignaturesNotice && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#10B981', fontWeight: 600 }}>
                  <CheckCircle2 size={14} />
                  <span>Signatures Saved!</span>
                </div>
              )}

              {savedLogosNotice && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#10B981', fontWeight: 600 }}>
                  <CheckCircle2 size={14} />
                  <span>Logos Saved!</span>
                </div>
              )}
            </div>

            {/* TAB 1: SIGNATURES & SIGNATORIES */}
            {customizerTab === 'signatures' && (
              <div>
                <p style={{ margin: '0 0 14px 0', fontSize: '12px', color: '#9CA3AF' }}>
                  Provide names, official titles, and signatures for the certification signatories. Upload signature transparent PNGs (recommended) or enter image URLs.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
                  {/* Signatory 1: Chief Electoral Commissioner */}
                  <div style={{ backgroundColor: '#171619', padding: '16px', borderRadius: '12px', border: '1px solid #2D2C31' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#FF9900', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Chief Electoral Commissioner / Returning Officer
                      </span>
                      {commSig && (
                        <button
                          type="button"
                          onClick={() => setCommSig('')}
                          style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '11px', cursor: 'pointer' }}
                        >
                          Clear Signature
                        </button>
                      )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div>
                        <label style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                          Signatory Full Name
                        </label>
                        <input
                          type="text"
                          value={commName}
                          onChange={(e) => setCommName(e.target.value)}
                          placeholder="e.g. Olapade Bukola"
                          style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '12px', boxSizing: 'border-box' }}
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                          Official Role Title
                        </label>
                        <input
                          type="text"
                          value={commTitle}
                          onChange={(e) => setCommTitle(e.target.value)}
                          placeholder="Chief Electoral Commissioner / Returning Officer"
                          style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '12px', boxSizing: 'border-box' }}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '4px' }}>
                        <div
                          style={{
                            width: '90px',
                            height: '48px',
                            borderRadius: '6px',
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #374151',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            flexShrink: 0,
                          }}
                        >
                          {commSig ? (
                            <img src={commSig} alt="Commissioner Signature" style={{ maxHeight: '42px', maxWidth: '84px', objectFit: 'contain' }} />
                          ) : (
                            <span style={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 600 }}>No Sig</span>
                          )}
                        </div>

                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              padding: '5px 10px',
                              borderRadius: '6px',
                              backgroundColor: '#1E1D21',
                              border: '1px solid #3B3A3E',
                              color: '#FFFFFF',
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: uploadingCommSig ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <Upload size={12} color="#FF9900" />
                            {uploadingCommSig ? 'Uploading...' : 'Upload Signature'}
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              disabled={uploadingCommSig}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleUploadSignature(file, 'commissioner');
                              }}
                            />
                          </label>
                          <input
                            type="text"
                            value={commSig}
                            onChange={(e) => setCommSig(e.target.value)}
                            placeholder="Or paste signature URL..."
                            style={{ width: '100%', marginTop: '6px', padding: '5px 8px', borderRadius: '5px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '11px', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Signatory 2: Faculty / Staff Electoral Observer */}
                  <div style={{ backgroundColor: '#171619', padding: '16px', borderRadius: '12px', border: '1px solid #2D2C31' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#FF9900', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Faculty / Staff Electoral Observer
                      </span>
                      {obsSig && (
                        <button
                          type="button"
                          onClick={() => setObsSig('')}
                          style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '11px', cursor: 'pointer' }}
                        >
                          Clear Signature
                        </button>
                      )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div>
                        <label style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                          Signatory Full Name
                        </label>
                        <input
                          type="text"
                          value={obsName}
                          onChange={(e) => setObsName(e.target.value)}
                          placeholder="e.g. Dr. J. O. Ogundele"
                          style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '12px', boxSizing: 'border-box' }}
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                          Official Role Title
                        </label>
                        <input
                          type="text"
                          value={obsTitle}
                          onChange={(e) => setObsTitle(e.target.value)}
                          placeholder="Faculty / Staff Electoral Observer"
                          style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '12px', boxSizing: 'border-box' }}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '4px' }}>
                        <div
                          style={{
                            width: '90px',
                            height: '48px',
                            borderRadius: '6px',
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #374151',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            flexShrink: 0,
                          }}
                        >
                          {obsSig ? (
                            <img src={obsSig} alt="Observer Signature" style={{ maxHeight: '42px', maxWidth: '84px', objectFit: 'contain' }} />
                          ) : (
                            <span style={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 600 }}>No Sig</span>
                          )}
                        </div>

                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              padding: '5px 10px',
                              borderRadius: '6px',
                              backgroundColor: '#1E1D21',
                              border: '1px solid #3B3A3E',
                              color: '#FFFFFF',
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: uploadingObsSig ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <Upload size={12} color="#FF9900" />
                            {uploadingObsSig ? 'Uploading...' : 'Upload Signature'}
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              disabled={uploadingObsSig}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleUploadSignature(file, 'observer');
                              }}
                            />
                          </label>
                          <input
                            type="text"
                            value={obsSig}
                            onChange={(e) => setObsSig(e.target.value)}
                            placeholder="Or paste signature URL..."
                            style={{ width: '100%', marginTop: '6px', padding: '5px 8px', borderRadius: '5px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '11px', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Signatory 3: Electpoll Platform Representative */}
                  <div style={{ backgroundColor: '#171619', padding: '16px', borderRadius: '12px', border: '1px solid #2D2C31' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#FF9900', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Electpoll Representative & Auditor
                      </span>
                      {repSig && (
                        <button
                          type="button"
                          onClick={() => setRepSig('')}
                          style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '11px', cursor: 'pointer' }}
                        >
                          Clear Signature
                        </button>
                      )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div>
                        <label style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                          Representative Full Name
                        </label>
                        <input
                          type="text"
                          value={repName}
                          onChange={(e) => setRepName(e.target.value)}
                          placeholder="e.g. Bukola Olapade"
                          style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '12px', boxSizing: 'border-box' }}
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                          Official Role Title
                        </label>
                        <input
                          type="text"
                          value={repTitle}
                          onChange={(e) => setRepTitle(e.target.value)}
                          placeholder="Electpoll Platform Representative & Systems Auditor"
                          style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '12px', boxSizing: 'border-box' }}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '4px' }}>
                        <div
                          style={{
                            width: '90px',
                            height: '48px',
                            borderRadius: '6px',
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #374151',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            flexShrink: 0,
                          }}
                        >
                          {repSig ? (
                            <img src={repSig} alt="Representative Signature" style={{ maxHeight: '42px', maxWidth: '84px', objectFit: 'contain' }} />
                          ) : (
                            <span style={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 600 }}>No Sig</span>
                          )}
                        </div>

                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              padding: '5px 10px',
                              borderRadius: '6px',
                              backgroundColor: '#1E1D21',
                              border: '1px solid #3B3A3E',
                              color: '#FFFFFF',
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: uploadingRepSig ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <Upload size={12} color="#FF9900" />
                            {uploadingRepSig ? 'Uploading...' : 'Upload Signature'}
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              disabled={uploadingRepSig}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleUploadSignature(file, 'representative');
                              }}
                            />
                          </label>
                          <input
                            type="text"
                            value={repSig}
                            onChange={(e) => setRepSig(e.target.value)}
                            placeholder="Or paste signature URL..."
                            style={{ width: '100%', marginTop: '6px', padding: '5px 8px', borderRadius: '5px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '11px', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
                  <button
                    type="button"
                    onClick={handleSaveSignatures}
                    style={{
                      padding: '7px 16px',
                      borderRadius: '7px',
                      backgroundColor: '#FF9900',
                      color: '#171619',
                      border: 'none',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Apply & Save Signatures to Election
                  </button>
                </div>
              </div>
            )}

            {/* TAB 2: HEADER LOGOS */}
            {customizerTab === 'logos' && (
              <div>
                <p style={{ margin: '0 0 14px 0', fontSize: '12px', color: '#9CA3AF' }}>
                  Upload up to two institutional or partner crests to appear beside the Electpoll logo at the top of the official PDF.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                  {/* Logo 1 Slot */}
                  <div style={{ backgroundColor: '#171619', padding: '14px', borderRadius: '12px', border: '1px solid #2D2C31' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#FF9900', textTransform: 'uppercase' }}>
                        Logo 1 (Primary Institution / Faculty)
                      </span>
                      {logo1 && (
                        <button
                          type="button"
                          onClick={() => setLogo1('')}
                          style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '11px', cursor: 'pointer' }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      {logo1 ? (
                        <img src={logo1} alt="Logo 1 Preview" style={{ width: '48px', height: '48px', objectFit: 'contain', backgroundColor: '#FFFFFF', borderRadius: '8px', padding: '4px' }} />
                      ) : (
                        <div style={{ width: '48px', height: '48px', borderRadius: '8px', backgroundColor: '#222126', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280' }}>
                          <ImageIcon size={20} />
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        <input
                          type="text"
                          value={logo1}
                          onChange={(e) => setLogo1(e.target.value)}
                          placeholder="Image URL or upload..."
                          style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '12px', boxSizing: 'border-box' }}
                        />
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', marginTop: '6px', fontSize: '11px', color: '#38BDF8', cursor: 'pointer', fontWeight: 600 }}>
                          <Upload size={11} /> {uploadingLogo1 ? 'Uploading...' : 'Upload Logo 1 (Cloudinary)'}
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleUploadLogo(file, 1);
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Logo 2 Slot */}
                  <div style={{ backgroundColor: '#171619', padding: '14px', borderRadius: '12px', border: '1px solid #2D2C31' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#FF9900', textTransform: 'uppercase' }}>
                        Logo 2 (Electoral Body / Association)
                      </span>
                      {logo2 && (
                        <button
                          type="button"
                          onClick={() => setLogo2('')}
                          style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '11px', cursor: 'pointer' }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      {logo2 ? (
                        <img src={logo2} alt="Logo 2 Preview" style={{ width: '48px', height: '48px', objectFit: 'contain', backgroundColor: '#FFFFFF', borderRadius: '8px', padding: '4px' }} />
                      ) : (
                        <div style={{ width: '48px', height: '48px', borderRadius: '8px', backgroundColor: '#222126', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280' }}>
                          <ImageIcon size={20} />
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        <input
                          type="text"
                          value={logo2}
                          onChange={(e) => setLogo2(e.target.value)}
                          placeholder="Image URL or upload..."
                          style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', backgroundColor: '#0E0D0F', border: '1px solid #2D2C31', color: '#FFFFFF', fontSize: '12px', boxSizing: 'border-box' }}
                        />
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', marginTop: '6px', fontSize: '11px', color: '#38BDF8', cursor: 'pointer', fontWeight: 600 }}>
                          <Upload size={11} /> {uploadingLogo2 ? 'Uploading...' : 'Upload Logo 2 (Cloudinary)'}
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleUploadLogo(file, 2);
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                  <button
                    type="button"
                    onClick={handleSaveCustomLogos}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '6px',
                      backgroundColor: '#27272A',
                      color: '#FFFFFF',
                      border: '1px solid #3F3F46',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Apply & Save Logos to Election
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Printable PDF Document Sheet (A4 Dimensions on Paper) */}
        <div className="official-pdf-document" id="official-report-printable-area">
          {/* ================================================================= */}
          {/* OFFICIAL REPORT HEADER BLOCK                                      */}
          {/* ================================================================= */}
          <header className="pdf-header-container pdf-avoid-break">
            {/* 1. Logos Top Row: Logo 1 (optional) | Logo 2 (optional) | Electpoll Logo */}
            <div className="pdf-logos-bar">
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                {logo1 && (
                  <div className="pdf-logo-wrapper">
                    <img src={logo1} alt="Institutional Crest" className="pdf-custom-logo" />
                  </div>
                )}

                {logo1 && (logo2 || true) && <div className="pdf-logo-divider" />}

                {logo2 && (
                  <>
                    <div className="pdf-logo-wrapper">
                      <img src={logo2} alt="Partner Crest" className="pdf-custom-logo" />
                    </div>
                    <div className="pdf-logo-divider" />
                  </>
                )}

                {/* Electpoll Official Brand Logo */}
                <div className="pdf-logo-wrapper">
                  <img
                    src="/assets/images/electpoll logo.png"
                    alt="Electpoll Official Logo"
                    className="pdf-electpoll-logo"
                  />
                </div>
              </div>
            </div>

            {/* 2. Title & Organization Block */}
            <div className="pdf-title-block">
              <div className="pdf-org-name">
                {(!election.organizationName || election.organizationName === "Law Students' Society, University of Ibadan" || election.organizationName === "Law Students' Society • University of Ibadan")
                  ? "LSS Electoral Commission 2025/26"
                  : election.organizationName}
              </div>
              <h1 className="pdf-election-title">
                {election.title}
              </h1>
              <div className="pdf-report-subtitle">
                Official Declaration of Results & Demographic Participation Intelligence Report
              </div>
            </div>

            {/* 3. Metadata Ribbon */}
            <div className="pdf-meta-ribbon">
              <div>
                <span className="pdf-meta-label">Printed On: </span>
                <span className="pdf-meta-val">{generationDate} at {generationTime}</span>
              </div>
              <div>
                <span className="pdf-meta-label">Election Status: </span>
                <span className="pdf-meta-val" style={{ textTransform: 'uppercase', color: election.status === 'active' ? '#047857' : '#B45309' }}>
                  {election.status}
                </span>
              </div>
              <div>
                <span className="pdf-meta-label">Ballot Integrity: </span>
                <span className="pdf-meta-val" style={{ color: '#047857' }}>Cryptographically Verified</span>
              </div>
            </div>

            {/* 4. Top KPI Cards */}
            <div className="pdf-kpi-grid">
              <div className="pdf-kpi-card">
                <div className="pdf-kpi-label">Overall Turnout Rate</div>
                <div className="pdf-kpi-val" style={{ color: '#FF9900' }}>{turnoutPercent}%</div>
                <div className="pdf-kpi-sub">{totalVoted} of {totalAccredited} Accredited</div>
              </div>

              <div className="pdf-kpi-card">
                <div className="pdf-kpi-label">Total Ballots Cast</div>
                <div className="pdf-kpi-val" style={{ color: '#10B981' }}>{totalVoted}</div>
                <div className="pdf-kpi-sub">Verified Cryptographic Ballots</div>
              </div>

              <div className="pdf-kpi-card">
                <div className="pdf-kpi-label">Accredited Registry</div>
                <div className="pdf-kpi-val" style={{ color: '#111827' }}>{totalAccredited}</div>
                <div className="pdf-kpi-sub">Eligible Student Voters</div>
              </div>

              <div className="pdf-kpi-card">
                <div className="pdf-kpi-label">Peak Velocity Window</div>
                <div className="pdf-kpi-val" style={{ color: '#3B82F6', fontSize: '17px' }}>
                  {peakHour?.hour || 'N/A'}
                </div>
                <div className="pdf-kpi-sub">{peakHour ? `${peakHour.count} Votes Cast` : 'Voting Window'}</div>
              </div>
            </div>
          </header>

          {/* ================================================================= */}
          {/* SECTION 1: CANDIDATE RESULTS (BALLOT TALLIES)                     */}
          {/* ================================================================= */}
          {(reportScope === 'all' || reportScope === 'results') && (
            <section className="pdf-section">
              <div className="pdf-section-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Award size={18} color="#FF9900" />
                  <h2 className="pdf-section-title">Official Candidate Results & Ballot Tallies</h2>
                </div>
                <span className="pdf-section-badge">{positions.length} Contested Offices</span>
              </div>

              {positions.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#6B7280', fontSize: '13px' }}>
                  No candidate results recorded.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {positions.map((pos, pIdx) => {
                    const sortedCandidates = [...(pos.candidates || [])].sort((a, b) => (b.votes || 0) - (a.votes || 0));
                    const winner = sortedCandidates[0];
                    const positionDisplayTitle = pos.positionName || (pos as any).positionTitle || `Position #${pIdx + 1}`;
                    
                    const isMulti = Boolean(pos.maxChoices && pos.maxChoices > 1);
                    const winningSeats = isMulti ? (pos.maxChoices || 1) : 1;

                    // Extract level number if present (e.g., "100 Level LSRC" -> "100")
                    const levelMatch = positionDisplayTitle.match(/(\d{3})/);
                    const levelNum = levelMatch ? levelMatch[1] : null;
                    const levelCohort = levelNum && levels.length > 0 ? levels.find(l => l.group.includes(levelNum)) : null;

                    // For LSRC / multi-choice positions, total votes is the total people that voted in that level
                    const totalPeopleVotedInLevel = isMulti
                      ? (levelCohort?.voted || pos.totalVoters || Math.max(...sortedCandidates.map(c => c.votes || 0), 1))
                      : (pos.totalVotes || (pos as any).totalPositionVotes || sortedCandidates.reduce((s, c) => s + (c.votes || 0), 0) || 1);

                    const percentageBase = totalPeopleVotedInLevel > 0 ? totalPeopleVotedInLevel : 1;

                    // Top winning candidates up to winningSeats
                    const winnersList = sortedCandidates
                      .filter(c => (c.votes || 0) > 0 && !c.isAbstain)
                      .slice(0, winningSeats);

                    return (
                      <div key={pos.positionId || pIdx} className="pdf-position-card pdf-avoid-break">
                        {/* Position Title Bar */}
                        <div className="pdf-pos-title-bar">
                          <div>
                            <span className="pdf-pos-number">Position #{pIdx + 1}</span>
                            <h3 className="pdf-pos-title">{positionDisplayTitle}</h3>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <span className="pdf-pos-votes-count">{totalPeopleVotedInLevel} Total Votes</span>
                            <span className="pdf-pos-rule">
                              Allowed: {pos.maxChoices || 1} choice(s){isMulti ? ` • ${winningSeats} Council Seats` : ''}
                            </span>
                          </div>
                        </div>

                        {/* Winner Highlight Callout */}
                        {isMulti && winningSeats > 1 ? (
                          winnersList.length > 0 && (
                            <div className="pdf-multi-winner-callout" style={{
                              backgroundColor: '#FFFBEB',
                              borderBottom: '1.5px solid #FDE68A',
                              padding: '12px 16px',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', borderBottom: '1px solid #FEF3C7', paddingBottom: '6px' }}>
                                <span style={{ fontSize: '11px', fontWeight: 800, color: '#B45309', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                                  ★ {winnersList.length} REPRESENTATIVES-ELECT ({winningSeats} COUNCIL SEATS)
                                </span>
                                <span style={{ fontSize: '10.5px', color: '#92400E', fontWeight: 600 }}>
                                  Top {winningSeats} Candidates Elected to Council
                                </span>
                              </div>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
                                gap: '8px 12px',
                              }}>
                                {winnersList.map((w, wIdx) => {
                                  const wPct = ((w.votes / percentageBase) * 100).toFixed(1);
                                  return (
                                    <div key={w.id || wIdx} style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      backgroundColor: '#FFFFFF',
                                      border: '1px solid #FCD34D',
                                      borderRadius: '6px',
                                      padding: '6px 10px',
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                                        <span style={{ fontSize: '11px', fontWeight: 800, color: '#B45309', minWidth: '22px' }}>
                                          #{wIdx + 1}
                                        </span>
                                        {w.image ? (
                                          <img src={w.image} alt={w.name} style={{ width: '26px', height: '26px', borderRadius: '50%', objectFit: 'cover' }} />
                                        ) : (
                                          <div style={{ width: '26px', height: '26px', borderRadius: '50%', backgroundColor: '#FEF3C7', color: '#B45309', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800 }}>
                                            {w.name?.[0] || 'C'}
                                          </div>
                                        )}
                                        <div style={{ fontSize: '11.5px', fontWeight: 700, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {w.name}
                                        </div>
                                      </div>
                                      <div style={{ textAlign: 'right', whiteSpace: 'nowrap', paddingLeft: '8px' }}>
                                        <div style={{ fontSize: '11.5px', fontWeight: 800, color: '#B45309' }}>{w.votes} Votes</div>
                                        <div style={{ fontSize: '10px', fontWeight: 700, color: '#D97706' }}>{wPct}%</div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )
                        ) : (
                          winner && winner.votes > 0 && !winner.isAbstain && (
                            <div className="pdf-winner-callout">
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                {winner.image ? (
                                  <img src={winner.image} alt={winner.name} className="pdf-winner-avatar" />
                                ) : (
                                  <div className="pdf-winner-avatar-fallback">
                                    {winner.name?.[0] || 'C'}
                                  </div>
                                )}
                                <div>
                                  <span className="pdf-winner-tag">
                                    ★ WINNER-ELECT
                                  </span>
                                  <div className="pdf-winner-name">
                                    {winner.name}
                                    {winner.nickname && <span className="pdf-winner-nickname"> "{winner.nickname}"</span>}
                                  </div>
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div className="pdf-winner-votes">{winner.votes} Votes</div>
                                <div className="pdf-winner-pct">
                                  {percentageBase > 0 ? ((winner.votes / percentageBase) * 100).toFixed(1) : 0}% of Ballot
                                </div>
                              </div>
                            </div>
                          )
                        )}

                        {/* Candidates Tally Table */}
                        <table className="pdf-table">
                          <thead>
                            <tr>
                              <th style={{ width: '45px' }}>Rank</th>
                              <th>Candidate Name</th>
                              <th style={{ width: '100px', textAlign: 'right' }}>Votes</th>
                              <th style={{ width: '90px', textAlign: 'right' }}>Share</th>
                              <th style={{ width: '180px' }}>Vote Distribution</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedCandidates.map((cand, cIdx) => {
                              const pct = percentageBase > 0 ? parseFloat(((cand.votes / percentageBase) * 100).toFixed(1)) : 0;
                              const isWinner = cIdx < winningSeats && (cand.votes || 0) > 0 && !cand.isAbstain;

                              return (
                                <tr key={cand.id || cIdx} className={isWinner ? 'pdf-row-winner' : ''}>
                                  <td style={{ fontWeight: 700, color: isWinner ? '#B45309' : '#6B7280' }}>
                                    #{cIdx + 1}
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      {cand.image && !cand.isAbstain && (
                                        <img src={cand.image} alt="" className="pdf-candidate-thumb" />
                                      )}
                                      <span style={{ fontWeight: 700, color: '#111827' }}>
                                        {cand.name}
                                        {cand.nickname && (
                                          <span style={{ fontWeight: 600, color: '#D97706', marginLeft: '4px' }}>
                                            "{cand.nickname}"
                                          </span>
                                        )}
                                        {cand.isAbstain && (
                                          <span style={{ fontSize: '11px', color: '#6B7280', marginLeft: '6px' }}>
                                            (Abstain Option)
                                          </span>
                                        )}
                                        {isWinner && (
                                          <span style={{
                                            fontSize: '9px',
                                            fontWeight: 800,
                                            color: '#B45309',
                                            backgroundColor: '#FEF3C7',
                                            border: '1px solid #FCD34D',
                                            borderRadius: '4px',
                                            padding: '1px 5px',
                                            marginLeft: '6px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.4px',
                                          }}>
                                            ELECT
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  </td>
                                  <td style={{ textAlign: 'right', fontWeight: 800, color: isWinner ? '#B45309' : '#111827' }}>
                                    {cand.votes}
                                  </td>
                                  <td style={{ textAlign: 'right', fontWeight: 600, color: isWinner ? '#B45309' : '#374151' }}>
                                    {pct}%
                                  </td>
                                  <td>
                                    <div className="pdf-bar-track">
                                      <div
                                        className="pdf-bar-fill"
                                        style={{
                                          width: `${Math.min(pct, 100)}%`,
                                          backgroundColor: isWinner ? '#FF9900' : '#3B82F6',
                                        }}
                                      />
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* ================================================================= */}
          {/* SECTION 2: DEMOGRAPHIC PARTICIPATION ANALYSIS                     */}
          {/* ================================================================= */}
          {(reportScope === 'all' || reportScope === 'demographics') && (
            <section className="pdf-section pdf-avoid-break">
              <div className="pdf-section-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <BarChart3 size={18} color="#FF9900" />
                  <h2 className="pdf-section-title">Demographic Participation Intelligence</h2>
                </div>
                <span className="pdf-section-badge">Segmented Turnout Analysis</span>
              </div>

              {/* 1. Academic Level & Gender Side-by-Side */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '24px' }}>
                {/* Level Breakdown */}
                <div className="pdf-demo-box pdf-avoid-break">
                  <h4 className="pdf-demo-box-title">Turnout by Academic Level</h4>
                  <table className="pdf-table">
                    <thead>
                      <tr>
                        <th>Cohort</th>
                        <th style={{ textAlign: 'right' }}>Accredited</th>
                        <th style={{ textAlign: 'right' }}>Voted</th>
                        <th style={{ textAlign: 'right' }}>Turnout</th>
                        <th style={{ width: '90px' }}>Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {levels.map((l) => (
                        <tr key={l.group}>
                          <td style={{ fontWeight: 700 }}>{l.group}</td>
                          <td style={{ textAlign: 'right' }}>{l.accredited}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>{l.voted}</td>
                          <td style={{ textAlign: 'right', fontWeight: 800, color: '#B45309' }}>{l.percentage}%</td>
                          <td>
                            <div className="pdf-bar-track">
                              <div className="pdf-bar-fill" style={{ width: `${Math.min(l.percentage, 100)}%`, backgroundColor: '#FF9900' }} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Gender Breakdown */}
                <div className="pdf-demo-box pdf-avoid-break">
                  <h4 className="pdf-demo-box-title">Turnout by Gender</h4>
                  <table className="pdf-table">
                    <thead>
                      <tr>
                        <th>Cohort</th>
                        <th style={{ textAlign: 'right' }}>Accredited</th>
                        <th style={{ textAlign: 'right' }}>Voted</th>
                        <th style={{ textAlign: 'right' }}>Turnout</th>
                        <th style={{ width: '90px' }}>Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {genders.map((g) => (
                        <tr key={g.group}>
                          <td style={{ fontWeight: 700 }}>{g.group}</td>
                          <td style={{ textAlign: 'right' }}>{g.accredited}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>{g.voted}</td>
                          <td style={{ textAlign: 'right', fontWeight: 800, color: g.group === 'Female' ? '#EC4899' : '#3B82F6' }}>
                            {g.percentage}%
                          </td>
                          <td>
                            <div className="pdf-bar-track">
                              <div
                                className="pdf-bar-fill"
                                style={{
                                  width: `${Math.min(g.percentage, 100)}%`,
                                  backgroundColor: g.group === 'Female' ? '#EC4899' : '#3B82F6',
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 2. Hall of Residence Breakdown */}
              <div className="pdf-demo-box pdf-avoid-break" style={{ marginBottom: '24px' }}>
                <h4 className="pdf-demo-box-title">Turnout by Hall of Residence / Accommodation</h4>
                <table className="pdf-table">
                  <thead>
                    <tr>
                      <th>Hall of Residence</th>
                      <th style={{ textAlign: 'right' }}>Accredited</th>
                      <th style={{ textAlign: 'right' }}>Voted</th>
                      <th style={{ textAlign: 'right' }}>Turnout</th>
                      <th style={{ width: '140px' }}>Turnout Proportion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {halls.map((h) => (
                      <tr key={h.group}>
                        <td style={{ fontWeight: 700 }}>{h.group}</td>
                        <td style={{ textAlign: 'right' }}>{h.accredited}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{h.voted}</td>
                        <td style={{ textAlign: 'right', fontWeight: 800, color: '#047857' }}>{h.percentage}%</td>
                        <td>
                          <div className="pdf-bar-track">
                            <div className="pdf-bar-fill" style={{ width: `${Math.min(h.percentage, 100)}%`, backgroundColor: '#10B981' }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 3. Hourly Turnout Velocity Timeline */}
              {hourlyData.length > 0 && (
                <div className="pdf-demo-box pdf-avoid-break" style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h4 className="pdf-demo-box-title" style={{ margin: 0 }}>
                      Hourly Voting Timeline & Velocity (Votes Per Hour)
                    </h4>
                    {peakHour && (
                      <span className="pdf-peak-flame-badge">
                        <Flame size={12} color="#D97706" /> Peak Hour: {peakHour.hour} ({peakHour.count} Votes)
                      </span>
                    )}
                  </div>

                  <table className="pdf-table">
                    <thead>
                      <tr>
                        <th>Time Interval</th>
                        <th style={{ textAlign: 'right' }}>Votes Cast</th>
                        <th style={{ textAlign: 'right' }}>Cumulative</th>
                        <th style={{ textAlign: 'right' }}>Share of Total</th>
                        <th style={{ width: '160px' }}>Hourly Volume</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hourlyData.map((h) => {
                        const isPeak = h.isPeak || (h.count > 0 && h.count === maxHourlyCount);
                        const relHeight = maxHourlyCount > 0 ? (h.count / maxHourlyCount) * 100 : 0;

                        return (
                          <tr key={h.hour} className={isPeak ? 'pdf-row-peak' : ''}>
                            <td style={{ fontWeight: 700, color: isPeak ? '#B45309' : '#111827' }}>
                              {h.hour} {isPeak && <span style={{ fontSize: '10px', color: '#D97706' }}>🔥 PEAK</span>}
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 800, color: isPeak ? '#B45309' : '#111827' }}>
                              {h.count}
                            </td>
                            <td style={{ textAlign: 'right', color: '#6B7280' }}>
                              {h.cumulative}
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>
                              {h.percentage}%
                            </td>
                            <td>
                              <div className="pdf-bar-track">
                                <div
                                  className="pdf-bar-fill"
                                  style={{
                                    width: `${Math.min(relHeight, 100)}%`,
                                    backgroundColor: isPeak ? '#FF9900' : '#3B82F6',
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* 4. Role-by-Role Demographic Cross-Tabulation Matrix */}
              {roles.length > 0 && (
                <div className="pdf-demo-box pdf-avoid-break">
                  <h4 className="pdf-demo-box-title">
                    Candidate Cross-Tabulation Matrix by Academic Level
                  </h4>
                  <p style={{ fontSize: '11px', color: '#6B7280', margin: '0 0 12px 0' }}>
                    Distribution of votes cast for each candidate segmented across class cohorts (100L – 500L).
                  </p>

                  <table className="pdf-table" style={{ fontSize: '12px' }}>
                    <thead>
                      <tr>
                        <th>Office & Candidate</th>
                        <th style={{ textAlign: 'center' }}>100L</th>
                        <th style={{ textAlign: 'center' }}>200L</th>
                        <th style={{ textAlign: 'center' }}>300L</th>
                        <th style={{ textAlign: 'center' }}>400L</th>
                        <th style={{ textAlign: 'center' }}>500L</th>
                        <th style={{ textAlign: 'right' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roles.map((r) => (
                        <React.Fragment key={r.positionId}>
                          <tr style={{ backgroundColor: '#F3F4F6' }}>
                            <td colSpan={7} style={{ fontWeight: 800, textTransform: 'uppercase', color: '#374151', fontSize: '11px', padding: '6px 10px' }}>
                              {r.positionName}
                            </td>
                          </tr>
                          {r.candidates.map((c) => {
                            const getVotes = (lvl: string) => {
                              const found = c.byLevel.find((s) => s.segment === `${lvl} Level` || s.segment === lvl);
                              return found ? found.votes : 0;
                            };
                            return (
                              <tr key={c.id}>
                                <td style={{ fontWeight: 600, paddingLeft: '16px' }}>
                                  {c.name}
                                </td>
                                <td style={{ textAlign: 'center' }}>{getVotes('100')}</td>
                                <td style={{ textAlign: 'center' }}>{getVotes('200')}</td>
                                <td style={{ textAlign: 'center' }}>{getVotes('300')}</td>
                                <td style={{ textAlign: 'center' }}>{getVotes('400')}</td>
                                <td style={{ textAlign: 'center' }}>{getVotes('500')}</td>
                                <td style={{ textAlign: 'right', fontWeight: 800 }}>{c.totalVotes}</td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {/* ================================================================= */}
          {/* OFFICIAL AUDIT CERTIFICATION FOOTER                              */}
          {/* ================================================================= */}
          <div className="pdf-footer-container pdf-avoid-break">
            <div className="pdf-sign-row">
              <div className="pdf-sign-box">
                {commSig ? (
                  <div className="pdf-signature-preview-wrap">
                    <img src={commSig} alt="Commissioner Signature" className="pdf-signature-img" />
                  </div>
                ) : (
                  <div className="pdf-signature-empty-spacer" />
                )}
                <div className="pdf-sign-line" />
                {commName ? <div className="pdf-sign-name">{commName}</div> : null}
                <div className="pdf-sign-role">{commTitle || 'Chief Electoral Commissioner / Returning Officer'}</div>
                <div className="pdf-sign-sub">Signature & Official Stamp</div>
              </div>

              <div className="pdf-sign-box">
                {obsSig ? (
                  <div className="pdf-signature-preview-wrap">
                    <img src={obsSig} alt="Observer Signature" className="pdf-signature-img" />
                  </div>
                ) : (
                  <div className="pdf-signature-empty-spacer" />
                )}
                <div className="pdf-sign-line" />
                {obsName ? <div className="pdf-sign-name">{obsName}</div> : null}
                <div className="pdf-sign-role">{obsTitle || 'Faculty / Staff Electoral Observer'}</div>
                <div className="pdf-sign-sub">Verification & Certification</div>
              </div>

              <div className="pdf-sign-box">
                {repSig ? (
                  <div className="pdf-signature-preview-wrap">
                    <img src={repSig} alt="Representative Signature" className="pdf-signature-img" />
                  </div>
                ) : (
                  <div className="pdf-signature-empty-spacer" />
                )}
                <div className="pdf-sign-line" />
                {repName ? <div className="pdf-sign-name">{repName}</div> : null}
                <div className="pdf-sign-role">{repTitle || 'Electpoll Platform Representative & Systems Auditor'}</div>
                <div className="pdf-sign-sub">Representative Signature • Date: {generationDate}</div>
              </div>
            </div>

            <div className="pdf-footer-bottom">
              <div>
                <strong>ELECTPOLL SECURE E-VOTING & DEMOGRAPHIC INTELLIGENCE PLATFORM</strong>
                <br />
                Cryptographically certified immutable ledger record
              </div>
              <div style={{ textAlign: 'right' }}>
                Report Verification ID: <code>ELEC-{election.slug || 'LSSUI'}-{new Date().getTime().toString(36).toUpperCase()}</code>
                <br />
                Page certified as complete official declaration
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
